import React, { useRef, useState } from 'react';
import { Tag } from 'lucide-react';
import type { ImageTag } from '../../services/api/imageTagService';
import type { BannerTagsController } from '../../pages/ads/components/useBannerTags';
import ImageTagPicker from './ImageTagPicker';
import TagPopover from './TagPopover';
import { tagCheckState } from './ImageTagMenu';
import { TaskyButton } from '../tasky';

interface BulkTagBarProps {
  controller: BannerTagsController;
  /** 選んでいるバナー */
  targets: { id: number; tags?: ImageTag[] }[];
  /** 「完了」で付け外しできたあとに呼ぶ。選択を終える */
  onDone: () => void;
}

/**
 * 一括選択で選んだバナーの下に浮かぶバー。ポップアップでは付けるタグを選ぶだけにし、「完了」でまとめて付け外しする。
 * 選んだタグを捨てるのは Esc で閉じたときだけ。外を押したり Tab で抜けたりしても残す（キーボードで「完了」へ移ると閉じるため）
 */
const BulkTagBar: React.FC<BulkTagBarProps> = ({ controller, targets, onDone }) => {
  const [open, setOpen] = useState(false);
  // タグの id → 選んだ全部に付ける(true)／全部から外す(false)。入っていないタグは今のまま
  const [picked, setPicked] = useState<Map<number, boolean>>(new Map());
  const [saving, setSaving] = useState(false);
  // 新しいタグを作っている数。作り終わるまで「完了」を押せない（押すと作ったタグが漏れる）
  const [creating, setCreating] = useState(0);
  // Esc で選んだタグを捨てた回数。作っている間に捨てられたら、作ったタグを付ける側に入れない
  const discardsRef = useRef(0);

  // 選んだあとで消されたタグは送らない（無いタグが1つでもあると、まとめて断られる）
  const existing = new Set(controller.tags.map((tag) => tag.id));
  const changes = Array.from(picked).filter(([id]) => existing.has(id));
  const add = changes.filter(([, attach]) => attach).map(([id]) => id);
  const remove = changes.filter(([, attach]) => !attach).map(([id]) => id);

  const checkState = (tagId: number) => {
    const attach = picked.get(tagId);
    if (attach === undefined) return tagCheckState(targets, tagId);
    return attach ? 'all' : 'none';
  };

  // 一部にだけ付いているタグは「付ける → 外す → 今のまま」、ほかは「付ける（外す） → 今のまま」と回す
  const toggle = (tag: ImageTag) => {
    setPicked((prev) => {
      const next = new Map(prev);
      const current = tagCheckState(targets, tag.id);
      const attach = prev.get(tag.id);
      if (attach === undefined) next.set(tag.id, current !== 'all');
      else if (attach && current === 'some') next.set(tag.id, false);
      else next.delete(tag.id);
      return next;
    });
  };

  const commit = async () => {
    setSaving(true);
    const ok = await controller.assign(targets.map((target) => target.id), { add, remove });
    setSaving(false);
    if (!ok) return;
    setOpen(false);
    setPicked(new Map());
    onDone();
  };

  return (
    // 色の多いバナーの上でも埋もれないよう、濃い地に大きいボタンを置いて下から出す
    <div className="pointer-events-auto flex animate-slide-in items-center gap-2 rounded-full bg-dark p-2 text-white shadow-2xl sm:gap-3 sm:pl-6">
      {/* スマホ幅では入りきらないので、枚数は「タグを付ける (n)」だけで見せる */}
      <span className="mr-1 hidden whitespace-nowrap text-base font-semibold sm:inline">{targets.length}枚を選択中</span>
      <TagPopover
        open={open}
        onOpenChange={(next, _event, reason) => {
          setOpen(next);
          if (!next && reason === 'escape-key') {
            discardsRef.current += 1;
            setPicked(new Map());
          }
        }}
        label="タグ"
        renderTrigger={({ ref, props }) => (
          <TaskyButton
            ref={ref as React.Ref<HTMLButtonElement>}
            type="button"
            variant="ghost"
            className="h-12 rounded-full border border-white/40 px-3 text-sm font-bold text-white hover:bg-white/10 hover:text-white sm:px-6 sm:text-base sm:[&_svg]:size-5"
            {...props}
          >
            <Tag />
            タグを付ける ({targets.length})
          </TaskyButton>
        )}
      >
        <ImageTagPicker
          tags={controller.tags}
          checkState={checkState}
          onToggle={toggle}
          onCreate={async (name) => {
            const discards = discardsRef.current;
            setCreating((count) => count + 1);
            const tag = await controller.createTag(name);
            setCreating((count) => count - 1);
            if (tag && discards === discardsRef.current) setPicked((prev) => new Map(prev).set(tag.id, true));
          }}
          onUpdate={controller.updateTag}
          onDelete={(tag) => {
            // 確認ダイアログの上にポップアップが重ならないよう先に閉じる
            setOpen(false);
            void controller.deleteTag(tag);
          }}
        />
      </TagPopover>
      <TaskyButton
        type="button"
        disabled={creating > 0 || add.length + remove.length === 0}
        isLoading={saving}
        onClick={() => void commit()}
        className="h-12 rounded-full px-5 text-sm font-bold shadow-md sm:px-8 sm:text-base"
      >
        完了
      </TaskyButton>
    </div>
  );
};

export default BulkTagBar;
